"use client";

import { Citrus, FlaskConical, Barrel, Wine } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "VERIFY",
  Icon: Citrus,
  description: "We conduct a rigorous vetting process to ensure all suppliers meet our global quality and compliance standards for trade."
}, {
  number: "02",
  title: "INTEGRATE",
  Icon: FlaskConical,
  description: "Our digital platform seamlessly connects your inventory systems with our global network for real-time trade execution."
}, {
  number: "03",
  title: "OPTIMIZE",
  Icon: Barrel,
  description: "Leverage our data-driven insights to refine your supply chain efficiency and maximize your market reach across borders."
}, {
  number: "04",
  title: "SCALE",
  Icon: Wine,
  description: "Expand your business footprint with confidence, backed by our secure, transparent, and high-volume trading infrastructure."
}];
export default function SupplierRegistration() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3883" id="supplierregistrationprocess" className="bg-white text-slate-900 relative overflow-hidden py-32 md:py-[200px]">
      {}
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-sans text-[10px] font-medium tracking-[0.3em] uppercase text-slate-900/50" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; THE PROCESS
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[#1a3a52]" />
              <span className="font-sans text-xs font-medium tracking-[0.25em] uppercase text-[#1a3a52]">
                Onboarding Protocol
              </span>
            </div>
            <h2 className="font-default font-bold text-4xl leading-[1.05] tracking-tight text-slate-900 sm:text-5xl md:text-6xl lg:text-[64px]">
              Streamlined trade
              <br />
              <span className="text-slate-900/70">
                for modern suppliers.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="font-default text-lg leading-relaxed text-slate-900/75 md:text-xl">
              At KTEX, we simplify the path to global markets. Our registration process is designed to be efficient, transparent, and secure, ensuring you can start trading with confidence.
            </p>
            <p className="mt-5 font-default text-base leading-relaxed text-slate-900/60">
              No unnecessary delays. No hidden barriers. Only a professional, structured approach to international commerce.
            </p>
          </div>
        </div>

        {}
        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {}
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-3 z-20 hidden lg:block">
                      <div className="flex items-center gap-0.5">
                        <span className="h-px w-1.5 bg-[#1a3a52]" />
                        <span className="h-px w-1 bg-[#1a3a52]/60" />
                        <span className="h-px w-0.5 bg-[#1a3a52]/30" />
                      </div>
                    </div>}

                  {}
                  <div className="relative h-[380px] overflow-hidden border border-slate-200 bg-white shadow-lg transition-all duration-500 group-hover:-translate-y-1 group-hover:border-[#1a3a52] group-hover:shadow-xl">
                    {}
                    <div className="absolute -top-4 -left-2 select-none">
                      <span className="font-sans text-[120px] font-extrabold leading-none tracking-tighter text-slate-900/5 transition-colors duration-[400ms] group-hover:text-[#1a3a52]/10" aria-hidden="true">
                        {step.number}
                      </span>
                    </div>

                    {}
                    <div className="relative flex h-full flex-col justify-between p-8 pt-32">
                      <Icon className="h-10 w-10 text-slate-900/80 transition-colors duration-300 group-hover:text-[#1a3a52]" strokeWidth={1} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-slate-900/30 transition-all duration-500 group-hover:w-16 group-hover:bg-[#1a3a52]" />
                        <h3 className="font-default font-bold text-2xl tracking-[0.15em] uppercase text-slate-900">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-relaxed text-slate-900/65">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {}
                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#1a3a52] to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-slate-200 pt-8 sm:flex-row sm:items-center">
            <p className="font-sans text-xs tracking-[0.25em] uppercase text-slate-900/50">
              Four steps &mdash; one global standard
            </p>
            <p className="font-default italic text-sm text-slate-900/60">
              Join the KTEX network today.
            </p>
          </div>
        </div>
      </div>
    </section>;
}